import { useState, useCallback } from 'react';
import { Plus, Trash2, Copy, ChevronDown } from 'lucide-react';

const FIELDS = [
  { path: 'country', label: 'Country', type: 'string' },
  { path: 'notice_kind', label: 'Notice Kind', type: 'string' },
  { path: 'portal_slug', label: 'Portal', type: 'string' },
  { path: 'sector', label: 'Sector', type: 'string' },
  { path: 'procurement_type', label: 'Procurement Type', type: 'string' },
  { path: 'procedure_type', label: 'Procedure Type', type: 'string' },
  { path: 'buyer.country', label: 'Buyer Country', type: 'string' },
  { path: 'buyer.name', label: 'Buyer Name', type: 'string' },
  { path: 'value.amount', label: 'Value Amount', type: 'number' },
  { path: 'value.currency', label: 'Value Currency', type: 'string' },
];

const STRING_OPS = ['eq', 'neq', 'in', 'not_in', 'starts_with', 'contains', 'is_null', 'is_not_null'];
const NUMBER_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_null', 'is_not_null'];

function opsForField(fieldPath) {
  const f = FIELDS.find((x) => x.path === fieldPath);
  return f?.type === 'number' ? NUMBER_OPS : STRING_OPS;
}

function makeLeaf() {
  return { field: 'country', op: 'eq', value: '' };
}

function makeGroup(kind) {
  return { [kind]: [makeLeaf()] };
}

export default function FilterBuilder({ value, onChange }) {
  const [copied, setCopied] = useState(false);

  const json = JSON.stringify(value, null, 2);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={wrapper}>
      <div style={builderPane}>
        <NodeEditor node={value} onChange={onChange} depth={0} />
      </div>
      <div style={previewPane}>
        <div style={previewHeader}>
          <span style={previewLabel}>JSON Preview</span>
          <button onClick={handleCopy} style={copyBtn}>
            <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre style={previewCode}>{json}</pre>
      </div>
    </div>
  );
}

function NodeEditor({ node, onChange, depth }) {
  if (!node) return null;

  // Group node (all_of / any_of)
  if (node.all_of || node.any_of) {
    const kind = node.all_of ? 'all_of' : 'any_of';
    const children = node[kind] || [];

    const toggleKind = () => {
      const newKind = kind === 'all_of' ? 'any_of' : 'all_of';
      onChange({ [newKind]: children });
    };

    const updateChild = (i, child) => {
      const next = [...children];
      next[i] = child;
      onChange({ [kind]: next });
    };

    const removeChild = (i) => {
      const next = children.filter((_, idx) => idx !== i);
      onChange({ [kind]: next.length ? next : [makeLeaf()] });
    };

    const addLeaf = () => onChange({ [kind]: [...children, makeLeaf()] });
    const addGroup = () => onChange({ [kind]: [...children, makeGroup('all_of')] });

    return (
      <div style={{ ...groupBox, marginLeft: depth > 0 ? 16 : 0 }}>
        <div style={groupHeader}>
          <button onClick={toggleKind} style={kindToggle}>
            {kind === 'all_of' ? 'ALL OF' : 'ANY OF'} <ChevronDown size={12} />
          </button>
          <div style={groupActions}>
            {depth < 3 && (
              <button onClick={addGroup} style={smallBtn}>+ Group</button>
            )}
            <button onClick={addLeaf} style={smallBtn}>+ Condition</button>
          </div>
        </div>
        {children.map((child, i) => (
          <div key={i} style={childRow}>
            <NodeEditor node={child} onChange={(c) => updateChild(i, c)} depth={depth + 1} />
            {children.length > 1 && (
              <button onClick={() => removeChild(i)} style={removeBtn}><Trash2 size={12} /></button>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Not node
  if (node.not) {
    return (
      <div style={{ ...groupBox, marginLeft: depth > 0 ? 16 : 0, borderColor: 'var(--danger)' }}>
        <div style={groupHeader}>
          <span style={{ ...kindToggle, color: 'var(--danger)' }}>NOT</span>
        </div>
        <NodeEditor node={node.not} onChange={(n) => onChange({ not: n })} depth={depth + 1} />
      </div>
    );
  }

  // Leaf node
  return <LeafEditor node={node} onChange={onChange} />;
}

function LeafEditor({ node, onChange }) {
  const ops = opsForField(node.field);
  const noValue = node.op === 'is_null' || node.op === 'is_not_null';

  return (
    <div style={leafRow}>
      <select
        value={node.field}
        onChange={(e) => onChange({ ...node, field: e.target.value })}
        style={selectStyle}
      >
        {FIELDS.map((f) => (
          <option key={f.path} value={f.path}>{f.label}</option>
        ))}
      </select>
      <select
        value={node.op}
        onChange={(e) => onChange({ ...node, op: e.target.value })}
        style={{ ...selectStyle, width: 120 }}
      >
        {ops.map((op) => (
          <option key={op} value={op}>{op}</option>
        ))}
      </select>
      {!noValue && (
        <input
          type="text"
          value={typeof node.value === 'string' ? node.value : JSON.stringify(node.value ?? '')}
          onChange={(e) => {
            let v = e.target.value;
            if (node.op === 'in' || node.op === 'not_in') {
              try { v = JSON.parse(v); } catch (_) { /* keep string */ }
            }
            onChange({ ...node, value: v });
          }}
          placeholder={node.op === 'in' || node.op === 'not_in' ? '["CH","DE"]' : 'value'}
          style={inputStyle}
        />
      )}
    </div>
  );
}

// Wrap a raw filter into a group if it's a leaf
export function ensureGroup(filter) {
  if (!filter) return makeGroup('all_of');
  if (filter.all_of || filter.any_of || filter.not) return filter;
  return { all_of: [filter] };
}

const wrapper = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', minHeight: 200 };
const builderPane = { overflow: 'auto' };
const previewPane = { background: 'var(--bg-3)', borderRadius: 'var(--r-2)', padding: 'var(--space-3)', display: 'flex', flexDirection: 'column' };
const previewHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' };
const previewLabel = { fontSize: 'var(--fs-11)', color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wider)' };
const copyBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--fs-11)', color: 'var(--fg-2)', background: 'none', border: 'none', cursor: 'pointer' };
const previewCode = { flex: 1, margin: 0, fontSize: 'var(--fs-12)', fontFamily: 'var(--font-mono)', color: 'var(--fg-1)', overflow: 'auto', whiteSpace: 'pre-wrap' };
const groupBox = { border: '1px solid var(--line-2)', borderRadius: 'var(--r-2)', padding: 'var(--space-2)', marginBottom: 'var(--space-1)' };
const groupHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' };
const kindToggle = { fontSize: 'var(--fs-11)', fontWeight: 600, color: 'var(--accent)', background: 'var(--accent-soft)', border: 'none', borderRadius: 'var(--r-1)', padding: '2px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 };
const groupActions = { display: 'flex', gap: 4 };
const smallBtn = { fontSize: 'var(--fs-11)', color: 'var(--fg-2)', background: 'var(--bg-3)', border: '1px solid var(--line-1)', borderRadius: 'var(--r-1)', padding: '2px 8px', cursor: 'pointer' };
const childRow = { display: 'flex', alignItems: 'flex-start', gap: 4 };
const removeBtn = { background: 'none', border: 'none', color: 'var(--fg-3)', cursor: 'pointer', padding: 4, flexShrink: 0, marginTop: 4 };
const leafRow = { display: 'flex', gap: 'var(--space-1)', alignItems: 'center', flex: 1 };
const selectStyle = { padding: '4px 8px', background: 'var(--bg-3)', border: '1px solid var(--line-1)', borderRadius: 'var(--r-1)', color: 'var(--fg-0)', fontSize: 'var(--fs-12)' };
const inputStyle = { flex: 1, padding: '4px 8px', background: 'var(--bg-3)', border: '1px solid var(--line-1)', borderRadius: 'var(--r-1)', color: 'var(--fg-0)', fontSize: 'var(--fs-12)' };
